import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Button,
    Stack,
    Tooltip,
    Paper,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { Icon } from "@iconify/react";
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import storeIcon from '@iconify/icons-mdi/storefront';
import accountIcon from '@iconify/icons-mdi/account-circle';
import serviceIcon from '@iconify/icons-mdi/content-cut';
import personIcon from '@iconify/icons-mdi/account-tie';
import noteIcon from '@iconify/icons-mdi/note-text';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';

const BookingDetailsPopup = ({ open, onClose, booking }) => {
    const navigate = useNavigate();
    const theme = useMuiTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!booking) return null;

    const formatDateTime = (iso) => {
        if (!iso) return "N/A";
        // Handle both ISO strings and Date objects
        const dt = typeof iso === 'string' ? DateTime.fromISO(iso) : DateTime.fromJSDate(new Date(iso));
        return dt.setZone("Asia/Kolkata").isValid ? dt.toFormat("dd/MM/yyyy, hh:mm a") : "Invalid Date";
    };

    const getStatusStyle = (status) => {
        const s = status?.toLowerCase();
        if (s === 'scheduled') return { color: '#3c0008', gradient: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', bg: '#ffffff', icon: 'mdi:calendar-clock' };
        if (s === 'completed') return { color: '#3c0008', gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', bg: '#ffffff', icon: 'mdi:check-decagram' };
        if (s === 'cancelled' || s === 'rejected') return { color: '#3c0008', gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', bg: '#ffffff', icon: 'mdi:close-circle' };
        if (s === 'pending') return { color: '#3c0008', gradient: 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)', bg: '#ffffff', icon: 'mdi:timer-sand' };
        if (s === 'in progress') return { color: '#3c0008', gradient: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)', bg: '#ffffff', icon: 'mdi:progress-clock' };
        if (s === 'approved') return { color: '#3c0008', gradient: 'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)', bg: '#ffffff', icon: 'mdi:check-circle' };
        return { color: '#3c0008', gradient: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)', bg: '#ffffff', icon: 'mdi:help-circle' };
    };

    const statusStyle = getStatusStyle(booking.status);

    const DetailBox = ({ icon, label, value, color }) => (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: '16px',
                bgcolor: 'white',
                border: '1px solid #f1f5f9'
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color
                    }}
                >
                    <Icon icon={icon} width={20} />
                </Box>

                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#94a3b8',
                            fontWeight: 800,
                            textTransform: 'uppercase'
                        }}
                    >
                        {label}
                    </Typography>

                    <Typography
                        sx={{
                            fontWeight: 700,
                            color: '#0f172a'
                        }}
                    >
                        {value}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );

    const handleCopyId = () => {
        if (booking._id) {
            navigator.clipboard.writeText(booking._id);
            toast.success("Reference ID copied to clipboard!");
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '92%', sm: 580 },
                maxHeight: '94vh',
                bgcolor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'saturate(200%) blur(30px)',
                borderRadius: '32px',
                boxShadow: '0 40px 80px -20px rgba(0, 0, 0, 0.3)',
                outline: 'none',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                '@keyframes modalEntrance': {
                    from: { opacity: 0, transform: 'translate(-50%, -45%) scale(0.92)' },
                    to: { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
                },
                animation: 'modalEntrance 0.6s cubic-bezier(0.2, 1, 0.3, 1)'
            }}>
                {/* Visual Header */}
                <Box sx={{
                    position: 'relative',
                    height: 160,
                    p: { xs: 3, sm: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    background: '#3C0008',
                    color: '#ffffff'
                    // Removed overflow: hidden to prevent cutting off the floating status card
                }}>
                    <Box sx={{
                        position: 'absolute',
                        top: -40,
                        right: -40,
                        width: 240,
                        height: 240,
                        borderRadius: '50%',
                        background: '#3C0008',
                        filter: 'blur(40px)',
                        pointerEvents: 'none'
                    }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                        <Box>
                            <Typography variant="h4" fontWeight="900" sx={{ letterSpacing: '-0.04em', mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                Appointment Details
                            </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Tooltip title="Click to copy Reference ID">
                                    <Box
                                        onClick={handleCopyId}
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '12px',
                                            bgcolor: 'rgba(255,255,255,0.15)',
                                            backdropFilter: 'blur(8px)',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                                            ID: {booking._id?.slice(-8).toUpperCase()}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                                <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.8, display: { xs: 'none', sm: 'block' } }}>
                                    • Created {booking.createdAt ? DateTime.fromISO(booking.createdAt).toFormat("dd/MM/yyyy") : 'Recently'}
                                </Typography>
                            </Stack>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#ffffff',
                                bgcolor: 'rgba(255,255,255,0.15)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'rotate(90deg)' },
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            <Icon icon={closeIcon} width={24} />
                        </IconButton>
                    </Stack>

                    {/* Status Floating Card */}
                    <Paper sx={{
                        position: 'absolute',
                        bottom: -35,
                        right: { xs: 20, sm: 40 },
                        px: 3,
                        py: 2,
                        borderRadius: '20px',
                        bgcolor: '#ffffff',
                        boxShadow: '0 15px 35px -10px rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        border: '1px solid #f1f5f9',
                        zIndex: 10
                    }}>
                        <Box sx={{
                            display: 'flex',
                            p: 1,
                            borderRadius: '12px',
                            bgcolor: statusStyle.bg,
                            color: statusStyle.color,
                            animation: 'pulse 2s infinite ease-in-out',
                            '@keyframes pulse': {
                                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                                '50%': { transform: 'scale(1.1)', opacity: 0.8 }
                            }
                        }}>
                            <Icon icon={statusStyle.icon} width={28} />
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 900, display: 'block', lineHeight: 1, mb: 0.5, letterSpacing: '0.05em' }}>
                                STATUS
                            </Typography>
                            <Typography variant="subtitle1" sx={{ color: statusStyle.color, fontWeight: 900, lineHeight: 1 }}>
                                {booking.status?.toUpperCase() || 'UNKNOWN'}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                <Box sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 8, sm: 9 }, overflowY: 'auto', flexGrow: 1 }}>
                    {/* Information Grid */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2.5,
                        mb: 4
                    }}>
                        <DetailBox
                            icon={accountIcon}
                            label="Customer"
                            value={booking.CustomerDetails?.[0]?.name || booking.customerId?.name || "Anonymous"}
                            color="#3C0008"
                        />
                        {/* <DetailBox
                            icon={serviceIcon}
                            label="Requested Service"
                            value={
                                (() => {
                                    const services = booking.serviceDetails || booking.ServiceDetails || [];
                                    console.log("BOOKING", booking);
                                    console.log("SERVICES", services);
                                    console.log("EMPLOYEES", booking?.EmployeeDetails);
                                    if (services.length > 0) return services.map(s => s.serviceName).join(", ");
                                    if (Array.isArray(booking.serviceId) && booking.serviceId.length > 0 && typeof booking.serviceId[0] === 'object') {
                                        return booking.serviceId.map(s => s.serviceName).join(", ");
                                    }
                                    return booking.serviceProvider || "Service Not Specified";
                                })()
                            }
                            color="#a52a2a"
                        />
                        <DetailBox
                            icon={personIcon}
                            label="Assigned Technician"
                            value={booking.EmployeeDetails?.[0]?.name || booking.employeeId?.name || "Unassigned"}
                            color="#6d000e"
                        /> */}
                        <DetailBox
                            icon={storeIcon}
                            label="Branch Location"
                            value={booking.storeDetails?.[0]?.storeName || booking.storeId?.storeName || "Main Branch"}
                            color="#800000"
                        />
                    </Box>

                    <Box
                        sx={{
                            mb: 4,
                            p: 3,
                            borderRadius: '16px',
                            background: '#fff',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                color: '#3C0008',
                                mb: 2
                            }}
                        >
                            Service & Employee Assignment
                        </Typography>

                        {(booking?.serviceDetails || booking?.ServiceDetails || []).map(
                            (service, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        p: 2,
                                        mb: 1,
                                        borderRadius: '10px',
                                        background: '#f8fafc'
                                    }}
                                >
                                    <Typography>
                                        {service?.serviceName}
                                    </Typography>

                                    <Typography>
                                        {booking?.EmployeeDetails?.[index]?.employeeName ||
                                            booking?.EmployeeDetails?.[index]?.name ||
                                            "Not Assigned"}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </Box>

                    {/* Schedule Card */}
                    <Box sx={{
                        p: 3,
                        borderRadius: '24px',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        mb: 4,
                        border: '1px solid #e2e8f0',
                        gap: 2
                    }}>
                        <Stack direction="row" spacing={2.5} alignItems="center">
                            <Box sx={{
                                p: 2,
                                borderRadius: '16px',
                                bgcolor: '#ffffff',
                                boxShadow: '0 8px 16px -4px rgba(0,0,0,0.08)',
                                color: '#3C0008'
                            }}>
                                <Icon icon="mdi:calendar-month-outline" width={32} />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.05em' }}>
                                    SCHEDULED APPOINTMENT
                                </Typography>
                                <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 900, mt: 0.5 }}>
                                    {formatDateTime(booking.timeSlot)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Remarks/Notes Section */}
                    <Box sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5, px: 1 }}>
                            <Icon icon={noteIcon} color="#64748b" width={20} />
                            <Typography variant="subtitle2" fontWeight="900" color="#334155" sx={{ letterSpacing: '0.05em' }}>
                                SPECIAL INSTRUCTIONS & NOTES
                            </Typography>
                        </Stack>
                        <Box sx={{
                            p: 3,
                            bgcolor: '#f8fafc',
                            borderRadius: '24px',
                            border: '1px solid #e2e8f0',
                            borderLeft: `6px solid ${statusStyle.color}`,
                            minHeight: 80
                        }}>
                            <Typography variant="body2" sx={{
                                color: '#475569',
                                fontWeight: 500,
                                lineHeight: 1.7,
                                fontStyle: booking.remarks ? 'normal' : 'italic'
                            }}>
                                {booking.remarks ? `"${booking.remarks}"` : "No special instructions provided for this booking."}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Action Footer */}
                <Box sx={{
                    p: { xs: 3, sm: 4 },
                    pt: 2,
                    borderTop: '1px solid #f1f5f9',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    gap: 2
                }}>
                    {booking.status?.toLowerCase() !== 'cancelled' && booking.status?.toLowerCase() !== 'expired' && booking.status?.toLowerCase() !== 'rejected' && (
                        <Button
                            fullWidth
                            onClick={() => {
                                onClose();
                                navigate(`/pos?appointmentId=${booking._id}`);
                            }}
                            variant="contained"
                            sx={{
                                py: 2,
                                borderRadius: '18px',
                                bgcolor: booking.status?.toLowerCase() === 'completed' ? '#059669' : '#3C0008',
                                color: '#ffffff',
                                fontWeight: 900,
                                fontSize: '0.95rem',
                                letterSpacing: '0.05em',
                                boxShadow: '0 12px 24px -6px rgba(15, 23, 42, 0.3)',
                                '&:hover': {
                                    bgcolor: booking.status?.toLowerCase() === 'completed' ? '#047857' : '#5c000d',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 15px 30px -8px rgba(15, 23, 42, 0.4)'
                                },
                                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                        >
                            {booking.status?.toLowerCase() === 'completed' ? "View Bill / POS" : "Create Bill"}
                        </Button>
                    )}
                    <Button
                        fullWidth
                        onClick={onClose}
                        variant="outlined"
                        sx={{
                            py: 2,
                            borderRadius: '18px',
                            borderColor: '#e2e8f0',
                            color: '#64748b',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            '&:hover': {
                                borderColor: '#cbd5e1',
                                bgcolor: '#f8fafc'
                            }
                        }}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default BookingDetailsPopup;
